/**
 * Dashboard section registry.
 *
 * Mirrors the nav registry pattern in `web/src/layout/nav.tsx`: each section
 * declares its `id`, chip `label`, chip `icon`, and a `gate(permissions)`
 * predicate against the **real `P` keys** in `web/src/utils/permissions.ts`.
 *
 * Gate table — derived from the prototype's `DASH_SECTIONS`
 * (the v2 prototype) reconciled with
 * Feature 01's `PERMISSION-MAPPING.md` and encoded in the SPEC
 * §"Section → permission-gate table".
 */

import React from 'react';
import MyTimeIcon from '@mui/icons-material/AccessTime';
import FinancialsIcon from '@mui/icons-material/Assessment';
import ProjectsIcon from '@mui/icons-material/Assignment';
import AllocationsIcon from '@mui/icons-material/AssignmentTurnedIn';
import TeamIcon from '@mui/icons-material/People';
import AvailabilityIcon from '@mui/icons-material/BeachAccess';
import BillingIcon from '@mui/icons-material/ReceiptLongOutlined';
import { P, hasAnyPermission } from '../../utils/permissions';

export type SectionId =
  | 'mytime'
  | 'financials'
  | 'billing'
  | 'projects'
  | 'allocations'
  | 'team'
  | 'availability';

export interface DashboardSection {
  id: SectionId;
  label: string;
  icon: React.ReactElement;
  /**
   * Returns true when this section is visible to the given permission set.
   * A section with no real gate (always-on) returns true unconditionally.
   */
  gate: (permissions: string[]) => boolean;
}

const always = () => true;

export const SECTIONS: DashboardSection[] = [
  {
    id: 'financials',
    label: 'Financials',
    icon: <FinancialsIcon fontSize="small" />,
    // Outer gate is `VIEW_PROJECT_VALUE`; individual £ tiles further gate on
    // their specific key (`VIEW_PROJECT_MARGIN` / `VIEW_RATES_*`) — see the
    // SPEC's "Financial-field gating" rule. Backend soft-masks via
    // `@SensitiveFields` for anything that does reach the client.
    gate: (perms) => hasAnyPermission(perms, P.VIEW_PROJECT_VALUE),
  },
  {
    id: 'billing',
    label: 'Billing',
    icon: <BillingIcon fontSize="small" />,
    // Payment milestones PM-23 (AC-16) — reports tier, same key as the
    // Reports Billing tab and the forecast endpoint (AC-R4): the block is
    // absent from the dashboard summary payload for anyone without it, so
    // the chip must not exist to toggle on a card with nothing to show.
    gate: (perms) => hasAnyPermission(perms, P.VIEW_REPORTS_FINANCIAL),
  },
  {
    id: 'mytime',
    label: 'My time',
    icon: <MyTimeIcon fontSize="small" />,
    gate: always,
  },
  {
    id: 'projects',
    label: 'Projects',
    icon: <ProjectsIcon fontSize="small" />,
    // Mirror the planner predicate that `ProjectsSection`'s body uses
    // (Projects starting + Allocations expiring widgets). Without this,
    // an Employee with VIEW_PROJECTS only would see a Projects chip with
    // nothing to toggle — the body renders null for non-planners. The
    // absent-not-masked rule says: no body, no chip.
    gate: (perms) =>
      hasAnyPermission(perms, P.MANAGE_PROJECTS, P.MANAGE_ALLOCATIONS),
  },
  {
    id: 'allocations',
    label: 'Allocations',
    icon: <AllocationsIcon fontSize="small" />,
    // Always-on — every employee has allocations (current or upcoming) to look at.
    gate: always,
  },
  // Note: Pipeline section dropped entirely (Phase 3b). The Project Pipeline
  // chart now lives inside Financials (Phase C). A stale `pipeline` id in
  // a persisted `pp:dashboard:sections:v1` entry is silently clamped out by
  // `useSectionFilters` on next read — no migration needed.
  {
    id: 'team',
    label: 'Team',
    icon: <TeamIcon fontSize="small" />,
    gate: (perms) =>
      hasAnyPermission(perms, P.VIEW_REPORTS_OPERATIONAL, P.VIEW_TIMESHEETS_ALL),
  },
  {
    id: 'availability',
    label: 'Availability',
    icon: <AvailabilityIcon fontSize="small" />,
    gate: always,
  },
];

/**
 * Returns the sections whose `gate` predicate passes for the given
 * permission set. The order is the registry order (stable, predictable
 * chip layout). A section absent from this list is **absent**, not masked
 * — neither chip nor body renders (the absent-not-masked rule from
 * Feature 01).
 */
export function visibleSections(permissions: string[]): DashboardSection[] {
  return SECTIONS.filter((section) => section.gate(permissions));
}

/**
 * Returns the set of section IDs visible to the given permissions. Useful
 * as the seed/clamp for the persisted user-toggle set so a section the
 * user lost permission for can never be toggled on by stale storage.
 */
export function visibleSectionIds(permissions: string[]): SectionId[] {
  return visibleSections(permissions).map((section) => section.id);
}
